import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Button, Input, Field, Alert } from '../components/ui';

/**
 * Requests a password reset link.
 *
 * Always reports success, even for an address with no account. Saying "no such
 * user" would turn this form into a way to discover who is a patient of a
 * psychology practice — which is exactly the kind of thing not to leak.
 */

const ForgotPasswordView = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: `${window.location.origin}/nueva-clave` }
    );
    setLoading(false);

    // Rate limiting is worth surfacing; "unknown address" deliberately is not.
    if (resetError && /rate|limit/i.test(resetError.message)) {
      setError(t('forgot.tooMany'));
      return;
    }
    setSent(true);
  };

  if (sent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">{t('forgot.sentTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600">{t('forgot.sentBody')}</p>
          <Link to="/ingresar">
            <Button className="mt-6 w-full">{t('activate.backToLogin')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-indigo-900">{t('common.appName')}</h1>
          <p className="mt-1 text-sm text-slate-600">{t('forgot.title')}</p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}
            <p className="text-sm text-slate-600">{t('forgot.explanation')}</p>

            <Field label={t('auth.email')} required>
              <Input
                type="email" value={email} autoComplete="email" required disabled={loading}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full">
              {t('forgot.submit')}
            </Button>
          </div>

          <p className="mt-6 border-t border-slate-100 pt-5 text-center text-sm">
            <Link to="/ingresar" className="font-semibold text-indigo-600 hover:underline">
              {t('activate.backToLogin')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordView;
